import React from 'react';
import { useIntl } from 'react-intl';
import { motion } from 'framer-motion';
import { TrendingDown, Minus } from 'lucide-react';
import InfoTooltip from '../common/InfoTooltip';
import { pctSymbol } from '../../utils/formatters';

export default function DCFGrowthAssumptions({ assumptions = [], className = '' }) {
  const intl = useIntl();

  const getGrowthColor = (rate) => {
    const r = Number(rate || 0);
    if (r > 0) return 'text-green-600';
    if (r < 0) return 'text-red-600';
    return 'text-succession-text-secondary';
  };

  const getGrowthBg = (rate) => {
    const r = Number(rate || 0);
    if (r > 0) return 'bg-green-500/10 border-green-500/20';
    if (r < 0) return 'bg-red-500/10 border-red-500/20';
    return 'bg-succession-bg-tertiary border-succession-border';
  };

  // 'unavailable' means the advisor had no usable revenue history: the 0% is an
  // assumption the valuation was run on, not a measured flat practice, so it must
  // not read as "No Growth".
  const growthCaption = (assumption) => {
    if (assumption.growthBasis === 'unavailable') {
      return intl.formatMessage({ id: 'succession.multi_line_valuation.dcf_growth_unavailable' });
    }
    return Number(assumption.growthRate) === 0
      ? intl.formatMessage({ id: 'succession.multi_line_valuation.dcf_no_growth' })
      : intl.formatMessage({ id: 'succession.multi_line_valuation.dcf_annual_growth' });
  };

  const getGrowthIcon = (rate) => {
    const r = Number(rate || 0);
    if (r < 0) return TrendingDown;
    return Minus;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className={`bg-succession-bg-primary border border-succession-border rounded-lg p-6 ${className}`}
    >
      <h3 className="text-lg font-semibold text-succession-text-primary mb-4 flex items-center gap-2">
        {intl.formatMessage({ id: 'succession.multi_line_valuation.dcf_growth_title' })}
        <InfoTooltip content="The projected annual growth rate for each business line, derived from historical revenue trends or manual input. These rates drive the revenue projections in the DCF model." />
      </h3>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {assumptions.map((assumption, index) => {
          const Icon = getGrowthIcon(assumption.growthRate);
          return (
            <motion.div
              key={assumption.line}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
              className={`border rounded-lg p-4 ${getGrowthBg(assumption.growthRate)}`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-succession-text-primary">{assumption.line}</span>
                <Icon className={`w-4 h-4 ${getGrowthColor(assumption.growthRate)}`} />
              </div>
              <div className={`text-2xl font-bold ${getGrowthColor(assumption.growthRate)}`}>
                {assumption.growthRate}{pctSymbol()}
              </div>
              <div className="text-xs text-succession-text-secondary mt-1">
                {growthCaption(assumption)}
              </div>
            </motion.div>
          );
        })}
      </div>
    </motion.div>
  );
}
